import React, { useEffect, useState } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import ModuleFrame, { Chip, useModuleTab } from '../../components/ModuleFrame';
import { AppFocusChips, useSelectedApp, withApp } from '../../components/AppSelector';
import { friendlyError, updateEntity, useEntityList } from '../../data/entities';
import { apiGet } from '../../utils/backendApi';
import NQubeModule from './NQubeModule';
import AgentIOModule from './AgentIOModule';
import TrainingCenter from './TrainingCenter';
import { LibraryLifecycle } from '../../components/LifecycleRail';

// Workflows & Agents — nQube (the visual workflow builder) and Agent IO
// (deployable agents) under one roof, plus the specialist role library the
// coordinator weaves. The selected app links to ONE pipeline: that is the
// "Build" step of the App Builder spine.
export default function WorkflowsModule() {
  const { org } = useOutletContext();
  const { app } = useSelectedApp();
  const [tab, setTab] = useModuleTab('workflows');
  const { docs: workspaces } = useEntityList('workspace', { org: org.id });
  const [roles, setRoles] = useState(null);
  useEffect(() => {
    if (tab !== 'roles' || roles) return;
    apiGet('/agent-library').then((r) => setRoles(r.roles || [])).catch(() => setRoles([]));
  }, [tab, roles]);

  const linkedTo = app?.links?.pipelineId && app?.links?.workspaceId
    ? `/orgs/${org.id}/ws/${app.links.workspaceId}/pipelines/${app.links.pipelineId}` : null;

  return (
    <ModuleFrame
      title="nQube"
      subtitle="Workflows & agents · Agent IO · Role library"
      accent="#f59e0b"
      note={`nQube module, tab ${tab}${app ? `, app "${app.name}" ${linkedTo ? 'has a linked workflow' : 'has NO linked workflow'}` : ', no app focused'}. Workflows open on their own canvas (/pipelines/:id). NoVA: build_pipeline, describe_pipeline, edit_pipeline, declare_requirement, run_pipeline, create_agent.`}
      help="Build the app's logic as a block workflow (User Request → Retrieve Knowledge → Query Data → AI Agent → Human Review / Present Result). Every side effect goes through a gate; agents get a voice, a look and an allowlist, then install with one token."
      context={(
        <>
          <AppFocusChips allowNone />
          {app && (linkedTo
            ? <Link to={linkedTo} className="mf-chip is-ok">workflow linked · open canvas</Link>
            : <Chip tone="warn">no workflow linked to {app.name}</Chip>)}
        </>
      )}
      lead={app ? <LibraryLifecycle org={org} app={app} moduleId="workflows" /> : null}
      tabs={[
        { id: 'workflows', label: 'Workflows' },
        { id: 'agents', label: 'Agent IO' },
        { id: 'training', label: 'Training Center' },
        { id: 'roles', label: 'Role library', count: roles?.length },
      ]}
      tab={tab}
      onTab={setTab}
      verbs={[
        { label: linkedTo ? 'Open the app canvas' : 'Build a workflow', hint: 'nQube', to: linkedTo || undefined, onClick: linkedTo ? undefined : () => setTab('workflows'), primary: true },
        { label: 'Create an agent', hint: 'Agent IO', onClick: () => setTab('agents') },
        { label: 'Train a neural net', hint: 'Training Center', onClick: () => setTab('training') },
        { label: 'Test the workflow', hint: 'QA & Testing', to: withApp(`/orgs/${org.id}/qa`, app) },
      ]}
    >
      {tab === 'workflows' && (
        <>
          {app && <LinkPipeline orgId={org.id} app={app} workspaces={workspaces || []} />}
          <NQubeModule />
        </>
      )}
      {tab === 'agents' && <AgentIOModule />}
      {tab === 'training' && <TrainingCenter org={org} />}
      {tab === 'roles' && (
        <ModuleFrame.Panel title="Specialist roles" meta="woven by the nQube coordinator; prompts stay server-side">
          {roles === null && <p className="cs-help">Loading…</p>}
          <div className="mf-grid-cards">
            {(roles || []).map((r) => (
              <div key={r.id} className="mf-card">
                <div className="mf-card-title">{r.name} <span className="mf-muted">{r.modelClass}</span></div>
                <div className="mf-muted">{r.blurb}</div>
              </div>
            ))}
          </div>
        </ModuleFrame.Panel>
      )}
    </ModuleFrame>
  );
}

// One collector per workspace so the app can pick a pipeline from any of them.
function LinkPipeline({ orgId, app, workspaces }) {
  const [options, setOptions] = useState({});
  const onDocs = React.useCallback((wsId, docs) => setOptions((m) => ({ ...m, [wsId]: docs })), []);
  const all = Object.entries(options).flatMap(([wsId, docs]) => docs.map((p) => ({ ...p, wsId })));
  const [err, setErr] = useState('');
  const value = app.links?.pipelineId ? `${app.links.workspaceId}/${app.links.pipelineId}` : '';
  const choose = async (v) => {
    const [workspaceId, pipelineId] = v ? v.split('/') : [null, null];
    setErr('');
    try {
      await updateEntity('app', { org: orgId }, app.id, { links: { ...(app.links || {}), workspaceId, pipelineId } });
    } catch (e) { setErr(friendlyError(e)); }
  };
  return (
    <div className="mf-row" style={{ marginBottom: 4 }}>
      {workspaces.map((ws) => <PipelineCollector key={ws.id} orgId={orgId} wsId={ws.id} onDocs={onDocs} />)}
      <span className="mf-muted">Workflow for <strong>{app.name}</strong></span>
      <select className="cs-input" value={value} onChange={(e) => choose(e.target.value)} aria-label="Link a workflow to this app">
        <option value="">— pick a workflow —</option>
        {all.map((p) => <option key={p.id} value={`${p.wsId}/${p.id}`}>{p.name}</option>)}
      </select>
      {!all.length && <span className="mf-muted">name one below — it is linked to {app.name} automatically</span>}
      {err && <span className="cs-form-error">{err}</span>}
    </div>
  );
}

function PipelineCollector({ orgId, wsId, onDocs }) {
  const { docs } = useEntityList('pipeline', { org: orgId, workspace: wsId });
  useEffect(() => { onDocs(wsId, docs || []); }, [wsId, docs, onDocs]);
  return null;
}
